import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { openCamera, openPhotoPicker } from "@/lib/camera";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Receipt, Camera, Loader2, Check, Trash2, Fuel, Wrench, Car,
  DollarSign, Plus, Filter
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  mileage: "Mileage", gas: "Gas/Fuel", tools: "Tools",
  materials: "Materials", uniforms: "Uniforms", insurance: "Insurance",
  vehicle_maintenance: "Vehicle Maint.", parking: "Parking",
  tolls: "Tolls", other: "Other"
};

const CATEGORY_ICONS = {
  mileage: Car, gas: Fuel, tools: Wrench, vehicle_maintenance: Wrench,
};

export default function ExpensesPage() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [category, setCategory] = useState("gas");
  const [amount, setAmount] = useState("");
  const [mileage, setMileage] = useState("");
  const [description, setDescription] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchExpenses = async () => {
    try {
      const { data } = await API.get("/expenses/my?limit=100");
      setExpenses(data.expenses);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchExpenses(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("category", category);
    formData.append("description", description);
    formData.append("amount", amount || "0");
    formData.append("mileage", mileage || "0");
    try {
      await API.post("/expenses/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      toast.success("Expense uploaded");
      fetchExpenses();
      setShowForm(false);
      setDescription(""); setAmount(""); setMileage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleApprove = async (id) => {
    try { await API.post(`/expenses/${id}/approve`); toast.success("Approved"); fetchExpenses(); }
    catch { toast.error("Failed"); }
  };

  const handleDelete = async (id) => {
    try { await API.delete(`/expenses/${id}`); toast.success("Deleted"); fetchExpenses(); }
    catch { toast.error("Failed"); }
  };

  const filtered = filterCat === "all" ? expenses : expenses.filter(e => e.category === filterCat);
  const totalAmount = filtered.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalMileage = filtered.reduce((sum, e) => sum + (e.mileage || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="expenses-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading font-bold text-zinc-900">Expenses</h2>
        <Button size="sm" onClick={() => setShowForm(!showForm)}
          className={showForm ? "bg-zinc-200 text-zinc-700" : "bg-red-600 hover:bg-red-700 text-white"}
          data-testid="add-expense-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> {showForm ? "Cancel" : "Add"}
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="border border-zinc-200">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Total</p>
            <p className="text-lg font-bold text-zinc-900">${totalAmount.toFixed(2)}</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Mileage</p>
            <p className="text-lg font-bold text-zinc-900">{totalMileage.toFixed(1)} mi</p>
          </CardContent>
        </Card>
        <Card className="border border-zinc-200">
          <CardContent className="p-3 text-center">
            <p className="text-[10px] text-zinc-400 uppercase font-bold">Count</p>
            <p className="text-lg font-bold text-zinc-900">{filtered.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Add Form */}
      {showForm && (
        <Card className="border-2 border-red-200 bg-red-50/30" data-testid="expense-add-form">
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-bold text-zinc-700">New Expense</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-zinc-500 font-bold">Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full mt-1 px-2 py-2 border border-zinc-300 rounded-lg text-xs bg-white"
                  data-testid="expense-cat-select">
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 font-bold">Amount ($)</label>
                <input type="number" step="0.01" value={amount}
                  onChange={e => setAmount(e.target.value)} placeholder="0.00"
                  className="w-full mt-1 px-2 py-2 border border-zinc-300 rounded-lg text-xs"
                  data-testid="expense-amt-input" />
              </div>
            </div>
            {(category === "mileage" || category === "gas") && (
              <div>
                <label className="text-[10px] text-zinc-500 font-bold">Mileage (miles)</label>
                <input type="number" step="0.1" value={mileage}
                  onChange={e => setMileage(e.target.value)} placeholder="0.0"
                  className="w-full mt-1 px-2 py-2 border border-zinc-300 rounded-lg text-xs"
                  data-testid="expense-mi-input" />
              </div>
            )}
            <div>
              <label className="text-[10px] text-zinc-500 font-bold">Description</label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)}
                rows={2} placeholder="Describe the expense..." className="mt-1 text-xs"
                data-testid="expense-desc-input" />
            </div>
            <div className="flex gap-2">
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleUpload} />
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleUpload} />
              <Button size="sm" onClick={() => openCamera(cameraInputRef)}
                disabled={uploading} className="flex-1 h-10 bg-zinc-900 hover:bg-zinc-800 text-white text-xs"
                data-testid="expense-photo-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Camera className="w-4 h-4 mr-1.5" /> Take Photo</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => openPhotoPicker(fileInputRef)}
                disabled={uploading} className="flex-1 h-10 text-xs"
                data-testid="expense-file-btn">
                Upload Receipt
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="w-3.5 h-3.5 text-zinc-400 flex-shrink-0" />
        <button onClick={() => setFilterCat("all")}
          className={`px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${filterCat === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>
          All
        </button>
        {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
          <button key={k} onClick={() => setFilterCat(k)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold whitespace-nowrap ${filterCat === k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600"}`}>
            {v}
          </button>
        ))}
      </div>

      {/* Expense List */}
      {filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map(exp => {
            const CatIcon = CATEGORY_ICONS[exp.category] || DollarSign;
            return (
              <Card key={exp.id} className="border border-zinc-200" data-testid={`exp-item-${exp.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {exp.filename && (
                      <img src={`${API_BASE}/api/expenses/${exp.id}/image`}
                        alt="Receipt" className="w-14 h-14 rounded-lg object-cover bg-zinc-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CatIcon className="w-3.5 h-3.5 text-zinc-400" />
                        <span className="text-xs font-medium text-zinc-700">{CATEGORY_LABELS[exp.category]}</span>
                        {exp.is_approved && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">
                            <Check className="w-2.5 h-2.5 mr-0.5" /> OK
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-zinc-600 truncate">{exp.description || exp.ai_description || "—"}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm font-bold text-zinc-900">${exp.amount?.toFixed(2)}</span>
                        {exp.mileage > 0 && <span className="text-[10px] text-zinc-500">{exp.mileage} mi</span>}
                        <span className="text-[10px] text-zinc-400">{new Date(exp.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!exp.is_approved && (
                        <Button size="sm" variant="outline" onClick={() => handleApprove(exp.id)}
                          className="text-[10px] h-6 px-2"><Check className="w-3 h-3" /></Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleDelete(exp.id)}
                        className="text-[10px] h-6 px-2 text-red-500"><Trash2 className="w-3 h-3" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border border-dashed border-zinc-200">
          <CardContent className="p-8 text-center">
            <Receipt className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">No expenses yet</p>
            <p className="text-xs text-zinc-400 mt-1">Tap "Add" to log mileage, gas, parts, and other expenses</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
